/* eslint-disable max-len */
import _ from 'underscore';

const V2_VERSION = '2.0';
const V3_VERSION = '3.0';

function prepareCampaignNameSuffixPreference(model) {
  return {
    '@odata.type': '#Model.ImportPrefixAndSuffixForStringProperty',
    EnablePrefixAndSuffix: model.ImportPrefixAndSuffixForStringProperty_EnablePrefixAndSuffix,
    SuffixString: model.ImportPrefixAndSuffixForStringProperty_SuffixString || '',
  };
}

function prepareSearchAndReplaceCampaignNamePreference(model) {
  return {
    '@odata.type': '#Model.ImportSearchAndReplaceForStringProperty',
    EnableSearchAndReplace: model.ImportSearchAndReplaceForStringProperty_EnableSearchAndReplace,
    SearchString: model.ImportSearchAndReplaceForStringProperty_SearchString || '',
    ReplaceString: model.ImportSearchAndReplaceForStringProperty_ReplaceString || '',
    IgnoreCaseInPlainText: true, // always set to true since we don't expose this option to customer
  };
}

function prepareUrlSuffixPreference(model) {
  return {
    '@odata.type': '#Model.ImportPrefixAndSuffixForStringProperty',
    EnablePrefixAndSuffix: model.ImportAppendDestUrlPreference_UseAppendString,
    SuffixString: model.ImportAppendDestUrlPreference_AppendStringForDestinationUrl || '',
  };
}

function prepareSearchAndReplaceUrlPreference(model) {
  return {
    '@odata.type': '#Model.ImportSearchAndReplaceForStringProperty',
    EnableSearchAndReplace: model.ImportSearchAndReplaceDestUrlPreference_UseSearchAndReplaceString,
    SearchString: model.ImportSearchAndReplaceDestUrlPreference_SearchStringForDestinationUrl || '',
    ReplaceString: model.ImportSearchAndReplaceDestUrlPreference_ReplaceStringForDestinationUrl || '',
    IgnoreCaseInPlainText: true, // always set to true since we don't expose this option to customer
  };
}

function prepareTrackingTemplateSuffixPreference(model) {
  return {
    '@odata.type': '#Model.ImportPrefixAndSuffixForStringProperty',
    EnablePrefixAndSuffix: model.AppendPreferencesForTrackingTemplate_UseAppendString,
    SuffixString: model.AppendPreferencesForTrackingTemplate_AppendStringForTrackingTemplate || '',
  };
}

function prepareSearchAndReplaceTrackingTemplatePreference(model) {
  return {
    '@odata.type': '#Model.ImportSearchAndReplaceForStringProperty',
    EnableSearchAndReplace:
      model.SearchAndReplacePreferencesForTrackingTemplate_UseSearchAndReplaceString,
    SearchString: model.SearchAndReplacePreferencesForTrackingTemplate_SearchStringForTrackingTemplate || '',
    ReplaceString: model.SearchAndReplacePreferencesForTrackingTemplate_ReplaceStringForTrackingTemplate || '',
    IgnoreCaseInPlainText: true, // always set to true since we don't expose this option to customer
  };
}

function prepareSearchAndReplaceCustomParametersPreference(model) {
  return {
    '@odata.type': '#Model.ImportSearchAndReplaceForStringProperty',
    EnableSearchAndReplace:
      model.SearchAndReplaceForCustomParameters_EnableSearchAndReplace,
    SearchString: model.SearchAndReplaceForCustomParameters_SearchString || '',
    ReplaceString: model.SearchAndReplaceForCustomParameters_ReplaceString || '',
    IgnoreCaseInPlainText: true, // always set to true since we don't expose this option to customer
  };
}

function prepareFindAndReplaceURLSuffixPreference(model) {
  return {
    '@odata.type': '#Model.ImportSearchAndReplaceForStringProperty',
    EnableSearchAndReplace:
      model.SearchAndReplaceForFinalURLSuffix_EnableSearchAndReplace,
    SearchString: model.SearchAndReplaceForFinalURLSuffix_SearchString || '',
    ReplaceString: model.SearchAndReplaceForFinalURLSuffix_ReplaceString || '',
    IgnoreCaseInPlainText: true, // always set to true since we don't expose this option to customer
  };
}

function prepareBMCSetting(model) {
  const flag = model.StoreIdForImportedProductAdExtensionsFlag;
  const id = parseInt(model.StoreIdForImportedProductAdExtensions, 10);

  if (flag && !_.isNaN(id)) {
    return id;
  }
  return null;
}

function prepareGoogleAccountFinalUrlSuffixValue(model, isMultiAccountImport) {
  if (model.ImportAccountUrlOptions) {
    return isMultiAccountImport ? 'finalUrlSuffix' : (model.FinalUrlSuffixValue || '');
  }
  return undefined;
}

function prepareGoogleAccountTrackingTemplateValue(model, isMultiAccountImport) {
  if (model.ImportAccountUrlOptions) {
    if (isMultiAccountImport) { return 'trackingTemplate'; }
    return model.TrackingTemplateValue ? decodeURIComponent(model.TrackingTemplateValue) : '';
  }
  return undefined;
}

function prepareNumericAdjustmentSetting(model, propertyName) {
  let adjustValue = model[`${propertyName}_AdjustmentValue`];

  if (_.isString(adjustValue)) {
    if (adjustValue.indexOf(',') >= 0) {
      adjustValue = adjustValue.replace(',', '.');
    }
    if (adjustValue.indexOf('.') >= 0 || adjustValue.indexOf(',') >= 0) {
      adjustValue = parseFloat(adjustValue);
    } else {
      adjustValue = parseInt(adjustValue, 10);
    }
  }

  if (model[`${propertyName}_AdjustmentDecrease`]) {
    adjustValue *= -1;
  }

  return {
    '@odata.type': '#Model.ImportAdjustmentForNumericProperty',
    EnableAdjustment: model[`${propertyName}_EnableAdjustment`],
    AdjustmentValue:
      model[`${propertyName}_EnableAdjustment`] ? adjustValue : null,
  };
}

// TODO: refactor later to pass params as objects
export function parseModelToImportUserPreference(
  model,
  isPageFeedEnabled = false,
  isMultiAccountImport = false,
  isDeviceTargetOptimizationEnabled = false,
  isAdScheduleUseSearcherTimezoneEnabled = false,
  isGoogleImageExtensionImportOptionEnabled = false,
  isPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled = false,
  isAudienceTargetForFBImportOptionEnabled = false,
  isGoogleLogoExtensionImportOptionEnabled = false,
  isGoogleUpdateAdUrlsImportOptionEnabled = false,
  isGoogleUpdateSitelinkUrlsImportOptionEnabled = false,
  isGoogleAccountNegativeKeywordsImportOptionEnabled = false,
  isGoogleAdCustomizerAttributesImportOptionEnabled = false,
  isGoogleConversionGoalsImportOptionEnabled = false,
  isBrandSuitabilityImportOptionEnabled = false
) {
  const preference = {
    '@odata.type': '#Model.ImportUserPreference',
    // import new options
    ImportNewEntities: model.ImportNewEntities,
    PauseNewCampaigns:
        model.ImportNewOption_ImportNewCampaignsAndMarkAsPaused,
    ImportNewCampaignsAndChildEntities: model.ImportNewOption_ImportNewCampaignsAndChildEntities,
    ImportNewAdGroupsAndChildEntitiesForExistingCampaigns:
        model.ImportNewOption_ImportNewAdGroupsAndChildEntitiesForExistingCampaigns,
    ImportNewAdsForExistingAdGroups: model.ImportNewOption_ImportNewAdsForExistingAdGroups,
    ImportNewKeywordsForExistingAdGroups:
        model.ImportNewOption_ImportNewKeywordsForExistingAdGroups,
    ImportNewNegativeKeywordsForExistingParents:
        model.ImportNewOption_ImportNewNegativeKeywordsForExistingParents,
    ImportNewNegativeKeywordList: model.ImportNewOption_ImportNewNegativeKeywordList,
    ImportNewLocationTargets: model.ImportNewOption_ImportNewLocationTargets,
    ImportNewAppAdExtensions: model.ImportNewOption_ImportNewAppAdExtensions,

    // import update options
    ImportUpdatedEntities: model.UpdateEntityPreferences,
    UpdateCampaignBudgets: model.ImportUpdateEntityPreference_UpdateCampaignBudget,
    UpdateBids: model.ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBids,
    UpdateNegativeKeywordLists:
      model.ImportUpdateEntityPreference_UpdateExistingNegativeKeywordList,
    UpdateNegativeSites: model.ImportUpdateEntityPreference_UpdateNegativeSites,
    UpdateKeywordUrls: model.ImportUpdateEntityPreference_UpdateKeywordDestinationUrl,
    UpdateTrackingTemplatesAndCustomParameters:
      model.ImportUpdateEntityPreference_UpdateTrackingTemplateAndCustomParams,
    UpdateLocationTargets: model.ImportUpdateEntityPreference_UpdateLocationTargets,
    UpdateStatusForCampaigns: model.ImportUpdateEntityPreference_UpdateStatusForExistingCampaigns,
    UpdateStatusForAdGroups: model.ImportUpdateEntityPreference_UpdateStatusForExistingAdGroups,
    UpdateStatusForAds: model.ImportUpdateEntityPreference_UpdateStatusForExistingAds,
    UpdateStatusForKeywords: model.ImportUpdateEntityPreference_UpdateStatusForExistingKeywords,
    UpdateLanguageForAdGroup: model.ImportUpdateEntityPreference_UpdateLanguageForExistingAdGroups,

    ImportDeletedEntities: model.ImportDeletedEntities,
    SetLowerBidsToAdCenterMinBids: model.DefaultBidsBelowMinToAdCenterMinBid,
    SetLowerCampaignBudgetsToAdCenterMinCampaignBudgets:
      model.DefaultCampaignBudgetBelowMinToAdCenterMinBudget,

    // adjustment for string properties including url, tracking template and campaign name
    PrefixAndSuffixForUrls: prepareUrlSuffixPreference(model),
    SearchAndReplaceForUrls: prepareSearchAndReplaceUrlPreference(model),
    PrefixAndSuffixForTrackingTemplates: prepareTrackingTemplateSuffixPreference(model),
    SearchAndReplaceForTrackingTemplates: prepareSearchAndReplaceTrackingTemplatePreference(model),
    PrefixAndSuffixForCampaignNames: prepareCampaignNameSuffixPreference(model),
    SearchAndReplaceForCampaignNames: prepareSearchAndReplaceCampaignNamePreference(model),
    // BMC settings
    AssociatedBingMerchantCenterStoreId: prepareBMCSetting(model),

    // special handling for below options: UI text is opposite of the enum
    // and this is desired behavior, so we pass back the opposite of what
    // is selected in the form
    UpdateCampaignNames: !model.ImportUpdateEntityPreference_UpdateNameForExistingCampaigns,
    ImportActiveOnlyNewCampaigns: !model.ImportNewOption_ImportNewActiveEntitiesOnly,

    // import options v2 options:
    ImportNewDayTimeTargets: model.ImportNewOption_ImportNewDayTimeTargets,
    ImportNewDeviceTargets: model.ImportNewOption_ImportNewDeviceTargets,
    ImportNewDemographicTargets: model.ImportNewOption_ImportNewDemographicTargets,
    ImportNewCallAdExtensions: model.ImportNewOption_ImportNewCallAdExtensions,
    ImportNewCalloutAdExtensions: model.ImportNewOption_ImportNewCalloutAdExtensions,
    ImportNewLocationAdExtensions: model.ImportNewOption_ImportNewLocationAdExtensions,
    ImportNewPriceAdExtensions: model.ImportNewOption_ImportNewPriceAdExtensions,
    ImportNewPromotionAdExtensions: model.ImportNewOption_ImportNewPromotionAdExtensions,
    ImportNewReviewAdExtensions: model.ImportNewOption_ImportNewReviewAdExtensions,
    ImportNewSitelinkAdExtensions: model.ImportNewOption_ImportNewSitelinkAdExtensions,
    ImportNewStructuredSnippetAdExtensions:
      model.ImportNewOption_ImportNewStructuredSnippetAdExtensions,
    UpdateDayTimeTargets: model.ImportUpdateEntityPreference_UpdateDayTimeTargets,
    UpdateDeviceTargets: model.ImportUpdateEntityPreference_UpdateDeviceTargets,
    UpdateDemographicTargets: model.ImportUpdateEntityPreference_UpdateDemographicTargets,
    UpdateAppAdExtensions: model.ImportUpdateEntityPreference_UpdateAppAdExtensions,
    UpdateCallAdExtensions: model.ImportUpdateEntityPreference_UpdateCallAdExtensions,
    UpdateCalloutAdExtensions: model.ImportUpdateEntityPreference_UpdateCalloutAdExtensions,
    UpdateLocationAdExtensions: model.ImportUpdateEntityPreference_UpdateLocationAdExtensions,
    UpdatePriceAdExtensions: model.ImportUpdateEntityPreference_UpdatePriceAdExtensions,
    UpdatePromotionAdExtensions: model.ImportUpdateEntityPreference_UpdatePromotionAdExtensions,
    UpdateReviewAdExtensions: model.ImportUpdateEntityPreference_UpdateReviewAdExtensions,
    UpdateSitelinkAdExtensions: model.ImportUpdateEntityPreference_UpdateSitelinkAdExtensions,
    UpdateStructuredSnippetAdExtensions:
      model.ImportUpdateEntityPreference_UpdateStructuredSnippetAdExtensions,
  };

  // google account properties
  preference.AdWordsAccountFinalUrlSuffix =
      prepareGoogleAccountFinalUrlSuffixValue(model, isMultiAccountImport);
  preference.AdWordsAccountTrackingTemplate = prepareGoogleAccountTrackingTemplateValue(model, isMultiAccountImport);

  preference.Version = V2_VERSION;

  preference.RaiseLowBidsToMinServingBidsForBsc = model.RaiseLowBidsToMinServingBidsForBsc;

  if (isDeviceTargetOptimizationEnabled) {
    preference.AutoDeviceBidOptimization = model.AutoDeviceBidOptimization;
  }
  if (isAdScheduleUseSearcherTimezoneEnabled) {
    preference.AdScheduleUseSearcherTimezone = model.AdScheduleUseSearcherTimezone;
  }
  if (isPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled) {
    preference.PauseAIMAdGroupIfAllAudienceCriterionNotImported = model.PauseAIMAdGroupIfAllAudienceCriterionNotImported;
  }
  if (isGoogleImageExtensionImportOptionEnabled) {
    preference.ImportNewImageAdExtensions = !!model.ImportNewOption_ImportNewImageAdExtensions;
    preference.UpdateImageAdExtensions = !!model.ImportUpdateEntityPreference_UpdateImageAdExtensions;
  }

  if (isGoogleLogoExtensionImportOptionEnabled) {
    preference.ImportNewLogoAdExtensions = !!model.ImportNewOption_ImportNewLogoAdExtensions;
    preference.UpdateLogoAdExtensions = !!model.ImportUpdateEntityPreference_UpdateLogoAdExtensions;
  }

  if (isGoogleUpdateAdUrlsImportOptionEnabled) {
    preference.UpdateAdUrls = !!model.ImportUpdateEntityPreference_UpdateAdUrls;
  }

  if (isGoogleUpdateSitelinkUrlsImportOptionEnabled) {
    preference.UpdateSitelinkUrls = !!model.ImportUpdateEntityPreference_UpdateSitelinkUrls;
  }

  if (isGoogleAccountNegativeKeywordsImportOptionEnabled) {
    preference.ImportNewAccountNegativeKeywords = !!model.ImportNewOption_ImportNewAccountNegativeKeywords;
  }

  if (isGoogleAdCustomizerAttributesImportOptionEnabled) {
    preference.UpdateAdCustomizerAttributes = !!model.ImportUpdateEntityPreference_UpdateAdCustomizerAttributes;
  }

  if (isGoogleConversionGoalsImportOptionEnabled) {
    preference.ImportNewConversionGoals = !!model.ImportNewOption_ImportNewConversionGoals;
    preference.UpdateConversionGoals = !!model.ImportUpdateEntityPreference_UpdateConversionGoals;
  }

  if (isBrandSuitabilityImportOptionEnabled) {
    preference.ImportNewBrandSuitability = !!model.ImportNewOption_ImportNewBrandSuitability;
    preference.UpdateBrandSuitability = !!model.ImportUpdateEntityPreference_UpdateBrandSuitability;
  }

  preference.ImportSearchAndDsaMixedCampaignAsSearchCampaign =
    model.ImportSearchAndDsaMixedCampaignAsSearchCampaign;

  preference.ImportNewKeywordUrls = model.ImportNewOption_ImportNewKeywordUrls;
  preference.ImportNewTrackingTemplateAndCustomParams =
      model.ImportNewOption_ImportNewTrackingTemplateAndCustomParams;
  preference.ImportNewNegativeSites = model.ImportNewOption_ImportNewNegativeSites;
  preference.UpdateAdGroupNetworkSetting =
      model.ImportUpdateEntityPreference_UpdateAdGroupNetworkSetting;

  preference.AdjustmentForBids = prepareNumericAdjustmentSetting(model, 'AdjustmentForBids');
  preference.AdjustmentForCampaignBudgets =
      prepareNumericAdjustmentSetting(model, 'AdjustmentForCampaignBudgets');

  preference.PauseParentIfNoLocationSupported = model.PauseParentIfNoLocationSupported;
  preference.EnableParentLocationMapping = !model.EnableParentLocationMapping;
  preference.Version = V3_VERSION;

  preference.ImportNewAudienceTargets = !!model.ImportNewOption_ImportNewAudienceTargets;
  preference.UpdateAudienceTargets = !!model.ImportUpdateEntityPreference_UpdateAudienceTargets;

  preference.SearchAndReplaceForCustomParameters = prepareSearchAndReplaceCustomParametersPreference(model);

  preference.SearchAndReplaceForFinalURLSuffix = prepareFindAndReplaceURLSuffixPreference(model);

  preference.AssociatedUETTagId = (preference.ImportNewAudienceTargets && model.AssociatedUETTagId &&
    model.AssociatedUETTagId !== '-1' && !!model.ImportUET
    && !!model.ImportNewEntities) ? Number(model.AssociatedUETTagId) : null;

  if (isPageFeedEnabled) {
    preference.ImportNewPageFeeds = !!model.ImportNewOption_ImportNewPageFeeds;
    preference.UpdatePageFeeds = !!model.ImportUpdateEntityPreference_UpdatePageFeeds;
  }

  preference.ImportNewAdCustomizerFeeds = !!model.ImportNewOption_ImportNewAdCustomizerFeeds;
  preference.UpdateAdCustomizerFeeds =
    !!model.ImportUpdateEntityPreference_UpdateAdCustomizerFeeds;
  preference.ImportAllAdUnderAnAdGroup = !!model.ImportNewOption_ImportAllAdUnderAnAdGroup;
  preference.ImportNewLabelsAndAssociations = (model.ImportNewLabelsAndAssociations != null)
    ? model.ImportNewLabelsAndAssociations
    : model.ImportNewEntities;
  preference.UpdateLabelsAndAssociations = (model.UpdateLabelsAndAssociations != null)
    ? model.UpdateLabelsAndAssociations
    : model.ImportUpdatedEntities;
  preference.ImportNewCustomFiltersForShoppingCampaigns =
      (model.ImportNewCustomFiltersForShoppingCampaigns != null)
        ? model.ImportNewCustomFiltersForShoppingCampaigns
        : model.ImportNewEntities;
  preference.UpdateCustomFiltersForShoppingCampaigns =
      (model.UpdateCustomFiltersForShoppingCampaigns != null)
        ? model.UpdateCustomFiltersForShoppingCampaigns
        : model.ImportUpdatedEntities;
  preference.UpdateBiddingStrategies = model.ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBidStrategies;

  if (!_.isUndefined(model.PauseAdGroupIfNoLocationSupported)
    && !_.isNull(model.PauseAdGroupIfNoLocationSupported)) {
    preference.PauseAdGroupIfNoLocationSupported = model.PauseAdGroupIfNoLocationSupported;
  }

  if (isAudienceTargetForFBImportOptionEnabled) {
    if (!_.isUndefined(model.NoAudienceTargetForFBImportOption)
      && !_.isNull(model.NoAudienceTargetForFBImportOption)) {
      preference.ImportNewAudienceTargets = !model.NoAudienceTargetForFBImportOption;
      preference.UpdateAudienceTargets = !model.NoAudienceTargetForFBImportOption;
    }
  }

  if (!_.isUndefined(model.RenameCampaignNameWithSuffix)
    && !_.isNull(model.RenameCampaignNameWithSuffix)) {
    preference.RenameCampaignNameWithSuffix = model.RenameCampaignNameWithSuffix;
  }

  if (!_.isUndefined(model.EnableAutoCurrencyConversion)
    && !_.isNull(model.EnableAutoCurrencyConversion)) {
    preference.EnableAutoCurrencyConversion = model.EnableAutoCurrencyConversion;
  }

  return preference;
}

